import { z } from 'zod';

import type { AiModelSettings } from '../modelSettings';

export const DECK_POWER_SIGNAL_IMPACTS = ['raises', 'lowers', 'neutral'] as const;

export const deckPowerSignalSchema = z.object({
  label: z.string().describe('Short name for the signal, e.g. "Fast mana" or "Mass land denial".'),
  detail: z.string().describe('One sentence citing concrete cards or counts from the decklist that justify this signal.'),
  impact: z.enum(DECK_POWER_SIGNAL_IMPACTS).describe('Whether this signal raises or lowers the rating, or is merely contextual.'),
});

// Field order is generation order: evidence first so the ratings are conditioned on it.
export const deckPowerAssessmentSchema = z.object({
  signals: z.array(deckPowerSignalSchema).describe('Three to six pieces of concrete evidence drawn from the decklist.'),
  bracket: z
    .number()
    .int()
    .min(1)
    .max(5)
    .nullable()
    .describe(
      'Official Commander bracket: 1 Exhibition, 2 Core, 3 Upgraded, 4 Optimized, 5 cEDH. Null for formats where brackets do not apply.',
    ),
  power_level: z.number().int().min(1).max(10).describe("Power from 1 to 10 relative to the deck's own format. Independent of bracket."),
  confidence: z.enum(['low', 'medium', 'high']).describe('How confident the rating is given the information available in the decklist.'),
  summary: z.string().describe('Two to four sentences of plain-language justification for the rating.'),
  key_cards: z.array(z.string()).describe('Card names, copied verbatim from the decklist, that most drive the rating.'),
});

export type DeckPowerSignal = z.infer<typeof deckPowerSignalSchema>;
export type DeckPowerAssessment = z.infer<typeof deckPowerAssessmentSchema>;

export type DeckPowerSignalImpact = (typeof DECK_POWER_SIGNAL_IMPACTS)[number];

/** What gets persisted onto the deck document. Written only by the Cloud Function. */
export interface DeckAiAssessment extends DeckPowerAssessment {
  run_id: string;
  model: string;
  /** ISO 8601 */
  created_at: string;
}

/** Input accepted by the `assessDeckPower` callable. */
export interface AssessDeckPowerInput {
  deckId: string;
  deckText: string;
  format: string | null;
  /** Admin-only overrides for testing alternative model settings. */
  settings?: Partial<AiModelSettings>;
}

export const COMMANDER_BRACKET_LABELS: Record<number, string> = {
  1: 'Exhibition',
  2: 'Core',
  3: 'Upgraded',
  4: 'Optimized',
  5: 'cEDH',
};
